import type { FormikTouched } from 'formik';
import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import AvatarComponent from '../../components/UI/Avatar/Avatar.component';
import ButtonComponent from '../../components/UI/Button/Button.component';
import ChangeAvatar from '../../components/UserProfileAvatar/AvatarEditor/ChangeAvatar';
import AvatarEditor from '../../components/UserProfileAvatar/AvatarEditor/index';
import { BUTTON_LABELS } from '../../constants/button-labels.constants';
import { sectionIDs } from '../../constants/section-ids.constants';
import type {
  LandingPageEnum,
  ThemeEnum,
} from '../../interfaces/user.interface';
import './user-profile-avatar.scss';

interface UserProfileAvatarProps {
  editInitialImage?: string | null;
  loadingAvatar: boolean;
  userAvatarKey: string;
  userAvatarUrl: string;
  newAvatar: any;
  file: File | null;
  handleChangeAvatar: () => void;
  handleEdit: () => void;
  handleCancelChangeAvatar: () => void;
  handleCancelEditAvatar: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveEdited: (editedImageData: string) => Promise<void>;
  handleUpload: () => Promise<undefined | string>;
  isChangingAvatar: boolean;
  isEditingAvatar: boolean;
  isValid: boolean;
  onSave: () => void;
  reloadAvatar?: boolean;
  touched: FormikTouched<{
    landingPage: LandingPageEnum;
    theme: ThemeEnum;
    avatar: string | undefined;
    id?: string;
    userId?: { _id: string };
    preferencesId?: { _id: string };
  }>;
  uploading: boolean;
  firstName?: string;
  lastName?: string;
}

const UserProfileAvatar = (props: UserProfileAvatarProps) => {
  const {
    editInitialImage,
    loadingAvatar,
    userAvatarKey,
    userAvatarUrl,
    newAvatar,
    handleEdit,
    handleChangeAvatar,
    handleSaveEdited,
    isEditingAvatar,
    isChangingAvatar,
    reloadAvatar,
    handleFileChange,
    touched,
    isValid,
    file,
    uploading,
    handleUpload,
    handleCancelChangeAvatar,
    handleCancelEditAvatar,
    firstName,
    lastName,
  } = props;

  const [avatarImgAccessible, setAvatarImgAccessible] = useState(
    !!(userAvatarKey || userAvatarUrl),
  );

  useEffect(() => {
    setAvatarImgAccessible(!!(userAvatarKey || userAvatarUrl));
  }, [userAvatarKey, userAvatarUrl]);

  const hasAvatar = avatarImgAccessible;

  // Create a local object URL for the selected file so the container shows a preview.
  // Revoke on cleanup to avoid memory leaks.
  const filePreviewUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  const displayAvatarUrl = filePreviewUrl ?? userAvatarUrl;

  return (
    <>
      {loadingAvatar ? (
        <div className="LoadingSpinner" />
      ) : (
        !isEditingAvatar && (
          <div
            className={'USER__PROFILE__AVATAR__WRAPPER'}
            id={sectionIDs.AVATAR}
          >
            <div className="AVATAR__IMAGE__CONTAINER">
              <AvatarComponent
                reloadAvatar={reloadAvatar as boolean}
                userAvatarKey={userAvatarKey}
                userAvatarUrl={displayAvatarUrl}
                firstName={firstName}
                lastName={lastName}
                onImageError={() => setAvatarImgAccessible(false)}
              />
            </div>
            <div className={'AVATAR__Buttons__CONTAINER__Narrow__Mode'}>
              {!isChangingAvatar && !hasAvatar && (
                <ButtonComponent
                  className={'AVATAR__Change__Button'}
                  variant={'primary'}
                  onClick={handleChangeAvatar}
                >
                  {BUTTON_LABELS.SELECT_IMAGE}
                </ButtonComponent>
              )}
              {!isChangingAvatar && hasAvatar && (
                <ButtonComponent
                  className={'AVATAR__Edit__Button'}
                  variant={'primary'}
                  onClick={handleEdit}
                >
                  {BUTTON_LABELS.EDIT_AVATAR}
                </ButtonComponent>
              )}
              {!isChangingAvatar && hasAvatar && (
                <ButtonComponent
                  className={'AVATAR__Change__Button'}
                  variant={'outline'}
                  onClick={handleChangeAvatar}
                >
                  {BUTTON_LABELS.CHANGE_AVATAR}
                </ButtonComponent>
              )}
            </div>
          </div>
        )
      )}
      {isChangingAvatar && !isEditingAvatar && (
        <ChangeAvatar
          file={file}
          handleCancelChangeAvatar={handleCancelChangeAvatar}
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          isChangingAvatar={isChangingAvatar}
          isValid={isValid}
          touched={touched}
          uploading={uploading}
        />
      )}
      {isEditingAvatar && (
        <AvatarEditor
          avatarKey={userAvatarKey}
          handleCancelEditAvatar={handleCancelEditAvatar}
          initialImage={
            editInitialImage ?? filePreviewUrl ?? (userAvatarUrl as string)
          }
          onSave={handleSaveEdited}
        />
      )}
    </>
  );
};

export default UserProfileAvatar;
